import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { adminService, type StudentStatus } from '../../services/admin.service';
import { useTheme } from '../../contexts/ThemeContext';

// ─── Theme-aware chart colors ─────────────────────────────────────────────────

function useChartColors() {
  const { theme } = useTheme();
  return useMemo(() => {
    const s = getComputedStyle(document.documentElement);
    const rgb  = (v: string) => `rgb(${s.getPropertyValue(v).trim()})`;
    const rgba = (v: string, a: number) => `rgba(${s.getPropertyValue(v).trim()}, ${a})`;
    return {
      label:   rgb('--color-on-surface-variant'),
      grid:    rgba('--color-outline-variant', 0.15),
      primary: rgb('--color-primary'),
      secondary: rgb('--color-secondary'),
      success: rgb('--color-success'),
      caution: rgb('--color-caution'),
    };
  }, [theme]);
}

// ─── Small helpers ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: StudentStatus }) {
  const styles: Record<StudentStatus, string> = {
    active:   'text-success bg-success/10 border-success/20',
    'at-risk': 'text-caution bg-caution/10 border-caution/20',
    inactive: 'text-on-surface-variant bg-surface-container border-outline-variant/20',
  };
  const labels: Record<StudentStatus, string> = { active: 'Active', 'at-risk': 'At Risk', inactive: 'Inactive' };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function KpiCard({ label, value, icon, tone }: { label: string; value: string | number; icon: string; tone: 'primary' | 'success' | 'caution' | 'neutral' }) {
  const toneMap = {
    primary:  'text-primary',
    success:  'text-success',
    caution:  'text-caution',
    neutral:  'text-on-surface-variant',
  };
  return (
    <div className="bg-surface-container-high rounded-xl p-5 flex items-center gap-4">
      <div className="p-2.5 rounded-lg bg-surface-container">
        <span className={`material-symbols-outlined ${toneMap[tone]} text-[22px]`}>{icon}</span>
      </div>
      <div>
        <p className="text-xs text-on-surface-variant font-medium">{label}</p>
        <p className="text-xl font-bold text-on-surface">{value}</p>
      </div>
    </div>
  );
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="material-symbols-outlined text-primary text-[20px]">{icon}</span>
      <h4 className="text-sm font-semibold text-on-surface uppercase tracking-wider">{title}</h4>
    </div>
  );
}

function MentorNote({ tone, icon, title, children }: {
  tone: 'success' | 'caution' | 'error' | 'primary';
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  const toneMap = {
    success: 'border-l-success bg-success/5',
    caution: 'border-l-caution bg-caution/5',
    error:   'border-l-error bg-error/5',
    primary: 'border-l-primary bg-primary/5',
  };
  const iconMap = {
    success: 'text-success',
    caution: 'text-caution',
    error:   'text-error',
    primary: 'text-primary',
  };
  return (
    <div className={`rounded-lg border-l-4 p-4 ${toneMap[tone]}`}>
      <div className="flex items-start gap-3">
        <span className={`material-symbols-outlined ${iconMap[tone]} text-[18px] mt-0.5 shrink-0`}>{icon}</span>
        <div>
          <p className="text-sm font-semibold text-on-surface">{title}</p>
          <div className="text-xs text-on-surface-variant mt-1 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

const DIFF_COLORS: Record<string, string> = {
  EASY:   'text-success',
  MEDIUM: 'text-caution',
  HARD:   'text-error',
};

const SUBMISSION_STATUS_COLORS: Record<string, string> = {
  ACCEPTED:            'text-success',
  WRONG_ANSWER:        'text-error',
  TIME_LIMIT_EXCEEDED: 'text-caution',
  RUNTIME_ERROR:       'text-error',
  PENDING:             'text-on-surface-variant',
};

// ─── Chart tooltips ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-container-highest border border-outline-variant/20 rounded-lg px-3 py-2 text-xs">
      <p className="text-on-surface-variant mb-1">{label}</p>
      <p className="text-on-surface font-semibold">{payload[0].value}</p>
    </div>
  );
}

function ActivityTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + p.value, 0);
  return (
    <div className="bg-surface-container-highest border border-outline-variant/20 rounded-lg px-3 py-2 text-xs min-w-[120px]">
      <p className="text-on-surface-variant mb-2 font-medium">{label}</p>
      {payload.filter((p) => p.value > 0).map((p) => (
        <div key={p.name} className="flex items-center justify-between gap-4 mb-0.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-on-surface-variant capitalize">{p.name}</span>
          </div>
          <span className="font-semibold text-on-surface">{p.value}</span>
        </div>
      ))}
      {total > 0 && (
        <div className="flex justify-between mt-1.5 pt-1.5 border-t border-outline-variant/15">
          <span className="text-on-surface-variant">Total</span>
          <span className="font-bold text-on-surface">{total}</span>
        </div>
      )}
    </div>
  );
}

// ─── Activity Monitor ───────────────────────────────────────────────────────────

const ACTIVITY_DAYS = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

function useActivityColors() {
  const colors = useChartColors();
  return useMemo(() => ({
    lessons: colors.primary,
    algo: colors.success,
    chat: colors.secondary,
    interviews: colors.caution,
  }), [colors]);
}

function ActivityMonitor({ studentId }: { studentId: string }) {
  const [days, setDays] = useState(30);
  const colors = useChartColors();
  const activityColors = useActivityColors();

  const { data: activity, isLoading } = useQuery({
    queryKey: ['admin-student-activity', studentId, days],
    queryFn: () => adminService.getStudentActivity(studentId, days),
  });

  const hasData = activity && activity.some((d) => d.total > 0);
  const totalEvents = activity?.reduce((s, d) => s + d.total, 0) ?? 0;
  const activeDays  = activity?.filter((d) => d.total > 0).length ?? 0;
  const tickInterval = days === 7 ? 0 : days === 30 ? 4 : 13;

  return (
    <div className="bg-surface-container-low rounded-xl overflow-hidden border border-outline-variant/10">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-[18px]">monitor_heart</span>
          <h4 className="text-xs font-semibold text-on-surface uppercase tracking-widest">Activity Monitor</h4>
        </div>
        <div className="flex gap-1">
          {ACTIVITY_DAYS.map((d) => (
            <button
              key={d.value}
              onClick={() => setDays(d.value)}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
                days === d.value
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex gap-8 px-6 pt-4 pb-2">
        <div>
          <p className="text-lg font-bold text-on-surface">{totalEvents}</p>
          <p className="text-[11px] text-on-surface-variant">Total events</p>
        </div>
        <div>
          <p className="text-lg font-bold text-on-surface">{activeDays}</p>
          <p className="text-[11px] text-on-surface-variant">Active days</p>
        </div>
        {activeDays > 0 && (
          <div>
            <p className="text-lg font-bold text-on-surface">{Math.round((totalEvents / activeDays) * 10) / 10}</p>
            <p className="text-[11px] text-on-surface-variant">Avg / active day</p>
          </div>
        )}
        {/* Legend inline */}
        <div className="ml-auto flex items-center gap-4 self-center">
          {Object.entries(activityColors).map(([key, color]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: color }} />
              <span className="text-[10px] text-on-surface-variant capitalize">{key}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="px-2 pb-4">
        {isLoading ? (
          <div className="h-44 mx-4 bg-surface-container rounded-lg animate-pulse" />
        ) : !hasData ? (
          <div className="h-44 flex flex-col items-center justify-center">
            <span className="material-symbols-outlined text-3xl text-on-surface-variant mb-2">bar_chart</span>
            <p className="text-sm text-on-surface-variant">No activity in the last {days} days.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={activity} barSize={days === 90 ? 3 : days === 30 ? 8 : 18} barGap={1} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
              <XAxis
                dataKey="date"
                interval={tickInterval}
                tick={{ fontSize: 10, fill: colors.label }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: string) =>
                  new Date(v + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })
                }
              />
              <YAxis
                tick={{ fontSize: 10, fill: colors.label }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<ActivityTooltip />} cursor={{ fill: colors.grid }} />
              <Bar dataKey="lessons"    stackId="a" fill={activityColors.lessons}    radius={[0, 0, 0, 0]} />
              <Bar dataKey="algo"       stackId="a" fill={activityColors.algo}       radius={[0, 0, 0, 0]} />
              <Bar dataKey="chat"       stackId="a" fill={activityColors.chat}       radius={[0, 0, 0, 0]} />
              <Bar dataKey="interviews" stackId="a" fill={activityColors.interviews} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ─── Interview score trend ─────────────────────────────────────────────────────

function InterviewScoreTrend({ timeline }: { timeline: Array<{ date: string; score: number }> }) {
  const colors = useChartColors();
  const data = timeline.map((d) => ({
    date: new Date(d.date).toLocaleDateString('en', { month: 'short', day: 'numeric' }),
    score: d.score,
  }));
  return (
    <div className="bg-surface-container-low rounded-xl overflow-hidden border border-outline-variant/10">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-outline-variant/10">
        <span className="material-symbols-outlined text-primary text-[18px]">trending_up</span>
        <h4 className="text-xs font-semibold text-on-surface uppercase tracking-widest">Interview Score Trend</h4>
      </div>
      <div className="px-2 py-4">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.label }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: colors.label }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Line
              type="monotone"
              dataKey="score"
              stroke={colors.primary}
              strokeWidth={2}
              dot={{ r: 4, fill: colors.primary, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: colors.primary, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Mentor Insights generator ─────────────────────────────────────────────────

function MentorInsights({ data }: { data: NonNullable<ReturnType<typeof adminService.getStudentDetail> extends Promise<infer T> ? T : never> }) {
  const { profile, kpis, training, algorithms, cv, interviewTimeline } = data;
  const insights: Array<{ tone: 'success' | 'caution' | 'error' | 'primary'; icon: string; title: string; body: string }> = [];

  // Training insight
  if (training.progressPct < 30) {
    insights.push({
      tone: 'caution',
      icon: 'school',
      title: 'Training progress is low',
      body: `Only ${training.progressPct}% of lessons completed. Consider scheduling a catch-up session to understand blockers and create a recovery plan.`,
    });
  } else if (training.progressPct >= 80) {
    insights.push({
      tone: 'success',
      icon: 'school',
      title: 'Training nearly complete',
      body: `Great progress at ${training.progressPct}%. Discuss next steps and advanced topics to keep momentum.`,
    });
  }

  // Algorithm insight
  if (algorithms.solved === 0 && algorithms.attempted === 0) {
    insights.push({
      tone: 'error',
      icon: 'code',
      title: 'No algorithm practice started',
      body: 'This student has not attempted any algorithm problems. Recommend starting with Easy problems and a consistent daily practice schedule.',
    });
  } else if (algorithms.solved === 0 && algorithms.attempted > 0) {
    insights.push({
      tone: 'caution',
      icon: 'code',
      title: 'Struggling with algorithms',
      body: `${algorithms.attempted} attempts but no solutions yet. Consider a pair-programming session on problem-solving strategy.`,
    });
  } else if (algorithms.solved >= 10) {
    insights.push({
      tone: 'success',
      icon: 'code',
      title: 'Strong algorithm practice',
      body: `${algorithms.solved} problems solved. Encourage moving to Medium/Hard problems if not already doing so.`,
    });
  }

  // Interview insight
  const lastScore = interviewTimeline.length > 0 ? interviewTimeline[interviewTimeline.length - 1].score : null;
  if (lastScore != null && lastScore < 50) {
    insights.push({
      tone: 'error',
      icon: 'mic',
      title: 'Interview performance needs support',
      body: `Latest interview score: ${lastScore}/100. Review the feedback together and run a mock interview focused on weak areas.`,
    });
  } else if (lastScore != null && lastScore >= 80) {
    insights.push({
      tone: 'success',
      icon: 'mic',
      title: 'Strong interview performance',
      body: `Latest interview score: ${lastScore}/100. Use this student as a peer mentor or focus on advanced behavioral interviews.`,
    });
  }

  // CV insight
  if (cv.resumeRedFlags.length > 0) {
    insights.push({
      tone: 'caution',
      icon: 'description',
      title: 'CV has red flags',
      body: `${cv.resumeRedFlags.length} issue${cv.resumeRedFlags.length > 1 ? 's' : ''} flagged. Review and prioritise the most critical fixes together.`,
    });
  }

  // Status insight
  if (profile.status === 'at-risk') {
    insights.push({
      tone: 'error',
      icon: 'person_alert',
      title: 'Student is at risk',
      body: 'Inactive for 14+ days with low progress. Reach out personally to re-engage and identify any external blockers.',
    });
  }

  if (insights.length === 0) {
    insights.push({
      tone: 'primary',
      icon: 'psychology',
      title: 'On track',
      body: 'No immediate concerns. Schedule a regular check-in to maintain momentum and discuss long-term goals.',
    });
  }

  return (
    <div className="space-y-3">
      {insights.map((insight, i) => (
        <MentorNote key={i} tone={insight.tone} icon={insight.icon} title={insight.title}>
          {insight.body}
        </MentorNote>
      ))}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function AdminStudentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openJobId, setOpenJobId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-student', id],
    queryFn: () => adminService.getStudentDetail(id!),
    enabled: !!id,
  });

  const activateMutation = useMutation({
    mutationFn: () => adminService.updateUserStatus(id!, 'ACTIVE'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-student', id] }),
  });

  if (isLoading || !data) {
    return (
      <div className="p-12 max-w-7xl mx-auto space-y-6">
        <div className="h-8 w-48 bg-surface-container-high rounded animate-pulse" />
        <div className="grid grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 bg-surface-container-high rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const { profile, kpis, training, algorithms, jobs, sessions, cv, interviewTimeline } = data;

  const trainingTone: 'primary' | 'success' | 'caution' =
    training.progressPct >= 75 ? 'success' : training.progressPct >= 40 ? 'primary' : 'caution';
  const algoTone: 'primary' | 'success' | 'caution' =
    algorithms.solved >= 10 ? 'success' : algorithms.solved > 0 ? 'primary' : 'caution';
  const interviewTone: 'primary' | 'success' | 'caution' =
    (kpis.avgInterviewScore ?? 0) >= 70 ? 'success' : (kpis.avgInterviewScore ?? 0) >= 50 ? 'primary' : 'caution';

  return (
    <div className="p-12 max-w-7xl mx-auto space-y-10">
      {/* Back */}
      <button
        onClick={() => navigate('/admin/students')}
        className="flex items-center gap-1 text-xs text-on-surface-variant hover:text-on-surface transition-colors"
      >
        <span className="material-symbols-outlined text-sm">arrow_back</span>
        All Students
      </button>

      {/* Profile header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold text-on-surface">
            {profile.name ?? profile.email}
          </h2>
          {profile.name && <p className="text-on-surface-variant">{profile.email}</p>}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <StatusBadge status={profile.status} />
            {profile.platformStatus === 'PENDING' && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold border text-caution bg-caution/10 border-caution/20">
                Pending Approval
              </span>
            )}
            <span className="text-xs text-on-surface-variant">
              Joined {new Date(profile.createdAt).toLocaleDateString()}
            </span>
            {profile.lastActiveAt && (
              <span className="text-xs text-on-surface-variant">
                Last active {new Date(profile.lastActiveAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        {profile.platformStatus === 'PENDING' && (
          <button
            onClick={() => activateMutation.mutate()}
            disabled={activateMutation.isPending}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-success text-white text-sm font-semibold hover:bg-success/90 disabled:opacity-60 transition-colors shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
            {activateMutation.isPending ? 'Activando...' : 'Activar cuenta'}
          </button>
        )}
      </div>

      {/* Mentor Insights — top priority */}
      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.15em] text-on-surface-variant font-semibold">
          Mentor Insights
        </h3>
        <MentorInsights data={data} />
      </section>

      {/* KPI row */}
      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.15em] text-on-surface-variant font-semibold">
          Key Metrics
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <KpiCard label="Training" value={`${kpis.trainingProgressPct}%`} icon="school" tone={trainingTone} />
          <KpiCard label="Algo Solved" value={kpis.algoSolved} icon="code" tone={algoTone} />
          <KpiCard label="Interviews" value={kpis.interviewCount} icon="mic" tone="neutral" />
          <KpiCard
            label="Avg Score"
            value={kpis.avgInterviewScore != null ? `${kpis.avgInterviewScore}/100` : '—'}
            icon="grade"
            tone={interviewTone}
          />
          <KpiCard
            label="CV Score"
            value={kpis.cvScore != null ? `${kpis.cvScore}/100` : '—'}
            icon="description"
            tone={kpis.cvScore != null && kpis.cvScore >= 70 ? 'success' : kpis.cvScore != null && kpis.cvScore >= 50 ? 'primary' : 'caution'}
          />
        </div>
      </section>

      {/* Activity Monitor */}
      <ActivityMonitor studentId={profile.id} />

      {/* Interview score trend */}
      {interviewTimeline.length > 1 && (
        <InterviewScoreTrend timeline={interviewTimeline} />
      )}

      {/* Training + Algorithms — side by side on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Training */}
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="school" title="Training Progress" />
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-on-surface-variant">{training.completedLessons} of {training.totalLessons} lessons</span>
              <span className="font-semibold text-on-surface">{training.progressPct}%</span>
            </div>
            <div className="w-full bg-surface-container h-2 rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-700 ${trainingTone === 'success' ? 'bg-success' : trainingTone === 'caution' ? 'bg-caution' : 'bg-primary'}`} style={{ width: `${training.progressPct}%` }} />
            </div>
          </div>
          {training.recentCompletions.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-xs text-on-surface-variant font-medium mb-2">Recent completions</p>
              {training.recentCompletions.map((c) => (
                <div key={c.lessonId} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-success text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                      check_circle
                    </span>
                    <span className="text-sm text-on-surface">{c.lessonTitle}</span>
                  </div>
                  <span className="text-xs text-on-surface-variant">
                    {c.completedAt ? new Date(c.completedAt).toLocaleDateString() : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Algorithms */}
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="code" title="Algorithm Practice" />
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Solved', count: algorithms.solved, tone: 'success' as const },
              { label: 'Attempted', count: algorithms.attempted, tone: 'caution' as const },
              { label: 'Not Started', count: algorithms.notStarted, tone: 'neutral' as const },
            ].map((s) => (
              <div key={s.label} className="text-center bg-surface-container rounded-lg p-4">
                <p className={`text-2xl font-bold ${s.tone === 'success' ? 'text-success' : s.tone === 'caution' ? 'text-caution' : 'text-on-surface-variant'}`}>{s.count}</p>
                <p className="text-xs text-on-surface-variant mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          {algorithms.recentSubmissions.length > 0 && (
            <>
              <p className="text-xs text-on-surface-variant font-medium mb-2">Recent submissions</p>
              <div className="space-y-2">
                {algorithms.recentSubmissions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${DIFF_COLORS[s.difficulty] ?? 'text-on-surface-variant'}`}>
                        {s.difficulty.slice(0, 1)}
                      </span>
                      <span className="text-sm text-on-surface">{s.problemTitle}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-medium ${SUBMISSION_STATUS_COLORS[s.status] ?? 'text-on-surface-variant'}`}>
                        {s.status.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-on-surface-variant">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Jobs & Interviews */}
      {jobs.length > 0 && (
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="work" title="Jobs & Interviews" />
          <div className="space-y-3">
            {jobs.map((job) => (
              <div key={job.id} className="bg-surface-container rounded-lg overflow-hidden border border-outline-variant/10">
                <button
                  onClick={() => setOpenJobId(openJobId === job.id ? null : job.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-container-high transition-colors"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="material-symbols-outlined text-on-surface-variant text-[18px]">work</span>
                    <div>
                      <p className="text-sm font-semibold text-on-surface">
                        {job.role} at {job.company}
                      </p>
                      <p className="text-xs text-on-surface-variant">
                        {job.status} · Applied {new Date(job.appliedAt).toLocaleDateString()}
                        {job.interviews.length > 0 && ` · ${job.interviews.length} interview${job.interviews.length !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant text-[18px]">
                    {openJobId === job.id ? 'expand_less' : 'expand_more'}
                  </span>
                </button>

                {openJobId === job.id && job.interviews.length > 0 && (
                  <div className="px-4 pb-4 space-y-4 border-t border-outline-variant/10 pt-4">
                    {job.interviews.map((iv) => (
                      <div key={iv.id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-on-surface">{iv.title}</p>
                          <div className="flex items-center gap-2">
                            {iv.score != null && (
                              <span className={`text-sm font-bold ${iv.score >= 70 ? 'text-success' : iv.score >= 50 ? 'text-caution' : 'text-error'}`}>
                                {iv.score}/100
                              </span>
                            )}
                            <span className="text-xs text-on-surface-variant">
                              {new Date(iv.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        {iv.summary && (
                          <p className="text-xs text-on-surface-variant leading-relaxed">{iv.summary}</p>
                        )}
                        {(iv.strengths.length > 0 || iv.improvements.length > 0) && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            {iv.strengths.length > 0 && (
                              <div className="bg-success/5 rounded-lg p-3">
                                <p className="text-xs font-semibold text-success mb-1">Strengths</p>
                                <ul className="space-y-0.5">
                                  {iv.strengths.map((s, i) => (
                                    <li key={i} className="text-xs text-on-surface-variant flex gap-1">
                                      <span>·</span>
                                      <span>{s}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {iv.improvements.length > 0 && (
                              <div className="bg-caution/5 rounded-lg p-3">
                                <p className="text-xs font-semibold text-caution mb-1">To Improve</p>
                                <ul className="space-y-0.5">
                                  {iv.improvements.map((s, i) => (
                                    <li key={i} className="text-xs text-on-surface-variant flex gap-1">
                                      <span>·</span>
                                      <span>{s}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sessions */}
      {sessions.length > 0 && (
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="calendar_month" title="Sessions" />
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-start justify-between py-2 border-b border-outline-variant/10 last:border-0">
                <div>
                  <p className="text-sm text-on-surface">{s.title}</p>
                  <p className="text-xs text-on-surface-variant">
                    {new Date(s.scheduledStart).toLocaleString()}
                    {s.tutorName && ` · with ${s.tutorName}`}
                  </p>
                  {s.notes && (
                    <p className="text-xs text-on-surface-variant mt-1 italic">“{s.notes}”</p>
                  )}
                </div>
                <span className={`text-xs font-medium shrink-0 mt-0.5 ${s.status === 'COMPLETED' ? 'text-success' : s.status === 'CANCELLED' ? 'text-error' : 'text-primary'}`}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CV & LinkedIn */}
      {(cv.resumeScore != null || cv.linkedinScore != null || cv.resumeSuggestions.length > 0 || cv.linkedinSuggestions.length > 0) && (
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="description" title="CV & LinkedIn" />
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-surface-container rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-primary">{cv.resumeScore != null ? `${cv.resumeScore}/100` : '—'}</p>
              <p className="text-xs text-on-surface-variant mt-1">Resume Score</p>
            </div>
            <div className="bg-surface-container rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-secondary">{cv.linkedinScore != null ? `${cv.linkedinScore}/100` : '—'}</p>
              <p className="text-xs text-on-surface-variant mt-1">LinkedIn Score</p>
            </div>
          </div>
          {cv.resumeVerdict && (
            <p className="text-sm text-on-surface-variant mb-3">{cv.resumeVerdict}</p>
          )}
          {cv.resumeRedFlags.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-error mb-1">Resume Red Flags</p>
              <ul className="space-y-1">
                {cv.resumeRedFlags.map((f, i) => (
                  <li key={i} className="text-xs text-on-surface-variant flex gap-2 items-start">
                    <span className="material-symbols-outlined text-error text-[14px] shrink-0 mt-0.5">warning</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cv.resumeSuggestions.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-on-surface-variant mb-1">Resume Suggestions</p>
              <ul className="space-y-1">
                {cv.resumeSuggestions.slice(0, 5).map((s, i) => (
                  <li key={i} className="text-xs text-on-surface-variant flex gap-2 items-start">
                    <span className="material-symbols-outlined text-primary text-[14px] shrink-0 mt-0.5">lightbulb</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cv.linkedinRedFlags.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-error mb-1">LinkedIn Red Flags</p>
              <ul className="space-y-1">
                {cv.linkedinRedFlags.map((f, i) => (
                  <li key={i} className="text-xs text-on-surface-variant flex gap-2 items-start">
                    <span className="material-symbols-outlined text-error text-[14px] shrink-0 mt-0.5">warning</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cv.linkedinSuggestions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-on-surface-variant mb-1">LinkedIn Suggestions</p>
              <ul className="space-y-1">
                {cv.linkedinSuggestions.slice(0, 5).map((s, i) => (
                  <li key={i} className="text-xs text-on-surface-variant flex gap-2 items-start">
                    <span className="material-symbols-outlined text-primary text-[14px] shrink-0 mt-0.5">lightbulb</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Onboarding data */}
      {profile.onboardingCompleted && profile.onboardingData && (
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="quiz" title="Onboarding Responses" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(profile.onboardingData).map(([key, value]) => (
              <div key={key} className="bg-surface-container rounded-lg p-3">
                <p className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium mb-1">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                <p className="text-sm text-on-surface">
                  {Array.isArray(value)
                    ? (value as unknown[]).map(v => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join(', ')
                    : typeof value === 'object' && value !== null
                      ? JSON.stringify(value)
                      : String(value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interests / learning goals */}
      {(profile.interests.length > 0 || profile.learningGoals) && (
        <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
          <SectionHeader icon="interests" title="Profile" />
          {profile.learningGoals && (
            <p className="text-sm text-on-surface-variant mb-3">{profile.learningGoals}</p>
          )}
          {profile.interests.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((interest, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-surface-container text-on-surface-variant border border-outline-variant/10">
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
